import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";

// ID-039 RC-CS-FEE-001 / RC-CS-TL-001: fee ledger + chronological request/revision timeline (server decides what each audience sees)
const money = (n) => `$${(n || 0).toFixed(2)}`;
const when = (iso) => (iso ? new Date(iso).toLocaleString() : "");
const PAY = { PAID: "text-status-available", WAIVED: "text-status-available", REFUNDED: "text-status-reserved", NOT_COMPLETED: "text-status-sold", UNPAID: "text-warmgrey" };

// ID-041: authenticated PDF download (server checks owner dealer / admin)
function PdfButton({ path, filename, label, testid }) {
  const go = async () => {
    try {
      const r = await api.get(path, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = filename; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch { toast.error("Could not download the PDF"); }
  };
  return <Button size="sm" variant="outline" className="h-7 px-2 text-[11px]" onClick={go} data-testid={testid}>{label}</Button>;
}

function FeeLedger({ fees, receipts, base }) {
  if (!fees.length) return <p className="mt-2 text-xs text-muted-foreground">No fees recorded.</p>;
  return (
    <div className="mt-2 space-y-3" data-testid="fee-ledger">
      {fees.map((f) => (
        <div key={f.fee_id} className="rounded-md border border-border p-3" data-testid={`fee-${f.fee_id}`}>
          <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
            <span className="font-medium text-charcoal">{f.label} · {f.revision_ref}</span>
            <span className="num" data-testid={`fee-${f.fee_id}-amount`}>{money(f.amount)} {f.currency}</span>
          </div>
          <p className={`mt-1 text-xs font-semibold ${PAY[f.payment_status] || ""}`} data-testid={`fee-${f.fee_id}-status`}>
            {f.state} · payment {f.payment_status}{f.transaction_ref ? ` · ${f.transaction_ref}` : ""}
          </p>
          <ul className="mt-2 space-y-1 text-xs text-warmgrey">
            {f.events.map((e) => (
              <li key={e.event_id} data-testid={`fee-event-${e.event_id}`}>
                {when(e.at)} — <b className="text-charcoal">{e.label}</b> {money(e.amount)}{e.reason ? ` — ${e.reason}` : ""}{e.internal_note ? ` (internal: ${e.internal_note})` : ""}{e.source ? ` [${e.source}]` : ""}
                {e.action === "PAID" && receipts.filter((r) => r.fee_id === f.fee_id).map((r) => (
                  <span key={r.id} className="ml-2 inline-flex items-center gap-1">
                    <PdfButton path={`${base}/receipts/${r.id}.pdf`} filename={`${r.receipt_number}.pdf`} label={`Download receipt ${r.receipt_number}`} testid={`receipt-download-${r.receipt_number}`} />
                    {r.current_status === "REFUNDED" && <span className="text-status-reserved">refunded</span>}
                  </span>
                ))}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

export function RequestTimeline({ base, refreshKey, requestNumber }) {
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`${base}/timeline`).then((r) => setData(r.data)).catch(() => setData({ timeline: [], fees: [], receipts: [] })); }, [base, refreshKey]);
  if (!data) return null;
  return (
    <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
      <div className="rounded-md border border-border bg-white p-6" data-testid="fee-ledger-panel">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Fees & payments</p>
          <PdfButton path={`${base}/payment-history.pdf`} filename={`${requestNumber}-payment-history.pdf`} label="Download payment history" testid="payment-history-download" />
        </div>
        <FeeLedger fees={data.fees} receipts={data.receipts || []} base={base} />
      </div>
      <div className="rounded-md border border-border bg-white p-6" data-testid="request-timeline">
        <p className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Timeline</p>
        <ol className="mt-2 max-h-[480px] space-y-2 overflow-y-auto border-l border-border pl-4">
          {data.timeline.map((t, i) => (
            <li key={i} className="text-xs" data-testid={`timeline-${i}`}>
              <p className="text-muted-foreground">{when(t.at)} · {t.revision_ref} · {t.actor_name}</p>
              <p className="font-medium text-charcoal" data-testid={`timeline-${i}-summary`}>{t.summary}</p>
              {t.details.map((d, j) => <p key={j} className="break-words text-warmgrey">{d}</p>)}
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}
